import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Building2, CheckCircle2, Clock3, RefreshCcw, XCircle } from 'lucide-react';
import AppPageHeader from '../shell/AppPageHeader';
import { fetchClientLicenses, type ClientLicenseDto } from '../../services/ClientLicensesApiClient';
import { shellMainPaddingX } from '../../utils/shellLayoutClasses';

export default function Dashboard() {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([] as ClientLicenseDto[]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null as string | null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const rows = await fetchClientLicenses();
        if (!mounted) return;
        setLicenses(rows);
      } catch (e) {
        if (!mounted) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load dashboard data');
        setLicenses([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const metrics = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const next90 = new Date(now);
    next90.setDate(now.getDate() + 90);
    const active = licenses.filter((x) => x.active).length;
    const inactive = licenses.length - active;
    const expiringSoon = licenses.filter((x) => {
      if (!x.expiresAt) return false;
      const d = new Date(`${x.expiresAt}T00:00:00`);
      return d >= now && d <= next90;
    }).length;
    const totalAnalyses = licenses.reduce((sum, x) => sum + (Number.isFinite(x.maxAnalyses) ? x.maxAnalyses : 0), 0);
    return {
      total: licenses.length,
      active,
      inactive,
      expiringSoon,
      totalAnalyses,
    };
  }, [licenses]);

  const expiringList = useMemo(
    () =>
      licenses
        .filter((x) => {
          if (!x.expiresAt) return false;
          const expiry = new Date(`${x.expiresAt}T00:00:00`);
          if (Number.isNaN(expiry.getTime())) return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return expiry >= today;
        })
        .sort((a, b) => (a.expiresAt || '').localeCompare(b.expiresAt || ''))
        .slice(0, 6),
    [licenses]
  );

  const formatExpiry = (iso: string | null) => {
    if (!iso) return '—';
    const parsed = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return iso;
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const expiresInDaysFromToday = (iso: string | null): { expired: boolean; days: number } | null => {
    if (!iso) return null;
    const expiry = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(expiry.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffMs = expiry.getTime() - today.getTime();
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return { expired: days < 0, days: Math.abs(days) };
  };

  return (
    <>
      <main className="flex-1 flex flex-col min-h-0 min-w-0">
        <AppPageHeader title="Dashboard" subtitle="Law firm license health and capacity" />
        <div className={`flex-1 overflow-auto pb-8 pt-3 sm:pt-4 ${shellMainPaddingX}`}>
          <div className="w-full space-y-6">
            {loadError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <div className="flex-1">{loadError}</div>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2 py-1 text-xs hover:bg-red-100"
                >
                  <RefreshCcw size={12} />
                  Retry
                </button>
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Total firms</p>
                  <Building2 size={18} className="text-emerald-700" />
                </div>
                <p className="mt-2 text-3xl font-medium text-gray-900">{loading ? '—' : metrics.total}</p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Active licenses</p>
                  <CheckCircle2 size={18} className="text-emerald-700" />
                </div>
                <p className="mt-2 text-3xl font-medium text-gray-900">{loading ? '—' : metrics.active}</p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Inactive licenses</p>
                  <XCircle size={18} className="text-amber-600" />
                </div>
                <p className="mt-2 text-3xl font-medium text-gray-900">{loading ? '—' : metrics.inactive}</p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Expiring (90 days)</p>
                  <Clock3 size={18} className="text-violet-700" />
                </div>
                <p className="mt-2 text-3xl font-medium text-gray-900">{loading ? '—' : metrics.expiringSoon}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-medium text-gray-900">License Capacity</h2>
                  <p className="text-sm text-gray-500">Total analysis slots allocated across all firms.</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/admin/licenses')}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg btn-primary text-sm font-medium focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2"
                >
                  Open licenses
                  <ArrowRight size={14} />
                </button>
              </div>
              <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Allocated capacity</div>
                <div className="mt-1 text-2xl font-medium text-emerald-900">
                  {loading ? '—' : metrics.totalAnalyses.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-medium text-gray-900">Upcoming Expirations</h2>
                  <p className="text-sm text-gray-500">Closest license expiration dates.</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/admin/licenses')}
                  className="text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  View all
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Firm</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr>
                        <td colSpan={4} className="px-5 py-8 text-center text-sm text-gray-500">
                          Loading dashboard data...
                        </td>
                      </tr>
                    ) : expiringList.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-5 py-8 text-center text-sm text-gray-500">
                          No licenses with expiration dates yet.
                        </td>
                      </tr>
                    ) : (
                      expiringList.map((x) => (
                        <tr key={x.id} className="hover:bg-gray-50/50">
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{x.firmName}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{x.contactEmail}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">
                            <div className="text-gray-900 font-medium">{formatExpiry(x.expiresAt)}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {(() => {
                                const parts = expiresInDaysFromToday(x.expiresAt);
                                if (!parts) return <span className="text-gray-400">—</span>;
                                if (parts.expired) {
                                  return (
                                    <span className="text-amber-800">
                                      Expired
                                      {parts.days > 0 ? (
                                        <span className="text-gray-500 font-normal"> ({parts.days}d ago)</span>
                                      ) : null}
                                    </span>
                                  );
                                }
                                return (
                                  <span className="tabular-nums">
                                    {parts.days}
                                    <span className="text-gray-500 font-normal"> days</span>
                                  </span>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                x.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {x.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
